import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminCard, AdmBtn } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { EventAdminTabs } from '../components/admin/EventAdminTabs'
import { useEvent } from '../hooks/usePotlucks'
import {
  useOccurrenceRsvps,
  useEventMetrics,
  usePublishEvent,
  useDraftEvent,
  useSetRsvpDeadline,
} from '../hooks/useChurchAdmin'

function formatDeadline(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

export default function EventOverviewPage() {
  const { slug, occurrenceId } = useParams<{ slug: string; occurrenceId: string }>()
  const { data, isLoading } = useOccurrenceRsvps(occurrenceId)

  const event = data?.data.event
  const occurrence = data?.data.occurrence
  const eventId = event?.id

  const { data: fullEventData } = useEvent(eventId)
  const fullEvent = fullEventData?.data
  const isDraft = fullEvent?.status === 'draft'

  const { data: metricsData } = useEventMetrics(isDraft ? undefined : eventId)
  const publish = usePublishEvent(eventId)
  const draft = useDraftEvent(eventId)
  const setDeadline = useSetRsvpDeadline(occurrenceId ?? '')

  const [showDeadlinePicker, setShowDeadlinePicker] = useState(false)
  const [deadlineInput, setDeadlineInput] = useState('')

  const rsvps = data?.data.rsvps ?? []
  const going = rsvps.filter((r) => r.attendanceStatus === 'going' && r.approvalStatus !== 'declined').length
  const maybe = rsvps.filter((r) => r.attendanceStatus === 'maybe' && r.approvalStatus !== 'declined').length
  const pending = rsvps.filter((r) => r.approvalStatus === 'pending').length

  const rsvpClosesAt = occurrence?.rsvpClosesAt ? new Date(occurrence.rsvpClosesAt) : null
  const rsvpIsClosed = rsvpClosesAt != null && rsvpClosesAt <= new Date()

  if (isLoading || !event || !occurrence || !eventId) {
    return (
      <AdminShell>
        <div className="flex items-center justify-center" style={{ height: '60vh' }}>
          <div className="text-[13px] text-muted">Loading…</div>
        </div>
      </AdminShell>
    )
  }

  return (
    <AdminShell>
      <EventAdminTabs
        slug={slug!}
        eventId={eventId}
        occurrenceId={occurrence.id}
        title={event.title}
        startsAt={occurrence.startsAt}
        active="overview"
      />

      {/* Draft banner / publish */}
      {isDraft && (
        <AdminCard style={{ marginBottom: 20 }}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="text-[14px] font-semibold text-ink">This event is a draft</div>
              <div className="text-[12.5px] text-muted mt-0.5">It isn't visible to anyone yet. Publish when you're ready.</div>
            </div>
            <AdmBtn
              variant="primary"
              size="lg"
              disabled={publish.isPending}
              onClick={() => publish.mutate(undefined)}
            >
              {publish.isPending ? 'Publishing…' : '🚀 Publish event'}
            </AdmBtn>
          </div>
        </AdminCard>
      )}

      <div className="grid gap-5" style={{ gridTemplateColumns: '1.5fr 1fr' }}>
        {/* Left column */}
        <div className="flex flex-col gap-5">
          {/* RSVP counts */}
          <AdminCard title="RSVPs">
            <div className="flex gap-8">
              <div>
                <div className="serif text-[34px] leading-none">{going}</div>
                <div className="text-[12px] text-muted mt-1">going</div>
              </div>
              <div>
                <div className="serif text-[34px] leading-none">{maybe}</div>
                <div className="text-[12px] text-muted mt-1">maybe</div>
              </div>
              <div>
                <div className="serif text-[34px] leading-none" style={{ color: pending > 0 ? 'var(--warn)' : undefined }}>{pending}</div>
                <div className="text-[12px] text-muted mt-1">pending</div>
              </div>
            </div>
            {pending > 0 && (
              <div className="mt-4">
                <Link to={`/churches/${slug}/occurrences/${occurrence.id}/host`} className="no-underline">
                  <AdmBtn variant="soft" size="sm">{pending} need approval — review in Guests</AdmBtn>
                </Link>
              </div>
            )}
          </AdminCard>

          {/* Metrics */}
          {!isDraft && metricsData && (
            <AdminCard title="Reach · last 30 days">
              <div className="flex gap-8">
                <div>
                  <div className="serif text-[28px] leading-none">{metricsData.data.views}</div>
                  <div className="text-[12px] text-muted mt-1">views</div>
                </div>
                <div>
                  <div className="serif text-[28px] leading-none">{metricsData.data.impressions}</div>
                  <div className="text-[12px] text-muted mt-1">impressions</div>
                </div>
                <div>
                  <div className="serif text-[28px] leading-none">{metricsData.data.rsvps}</div>
                  <div className="text-[12px] text-muted mt-1">RSVPs</div>
                </div>
              </div>
            </AdminCard>
          )}

          {/* Quick actions */}
          <AdminCard title="Manage">
            <div className="flex flex-wrap gap-2.5">
              <Link to={`/churches/${slug}/occurrences/${occurrence.id}/host`} className="no-underline">
                <AdmBtn variant="soft" icon="users">Guest list</AdmBtn>
              </Link>
              <Link to={`/churches/${slug}/events/${eventId}/edit`} className="no-underline">
                <AdmBtn variant="soft" icon="edit">Edit details</AdmBtn>
              </Link>
              <Link to={`/churches/${slug}/occurrences/${occurrence.id}/day-of`} className="no-underline">
                <AdmBtn variant="soft" icon="check">Day-of mode</AdmBtn>
              </Link>
            </div>
          </AdminCard>
        </div>

        {/* Right rail */}
        <div className="flex flex-col gap-5">
          {/* At a glance */}
          <AdminCard title="Details">
            <div className="flex flex-col gap-3">
              <div className="flex items-start gap-2.5">
                <span className="text-muted mt-0.5"><AdmIcn name="cal" size={15} /></span>
                <div className="text-[13px] text-ink">{formatDeadline(occurrence.startsAt)}</div>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-muted mt-0.5"><AdmIcn name="pin" size={15} /></span>
                <div className="text-[13px] text-ink">{fullEvent?.addressText ?? '—'}</div>
              </div>
            </div>
          </AdminCard>

          {/* RSVP deadline */}
          {!isDraft && (
            <AdminCard title="RSVP deadline">
              {rsvpIsClosed ? (
                <div>
                  <div className="text-[12.5px] text-warn font-medium mb-2">RSVPs closed · {formatDeadline(occurrence.rsvpClosesAt!)}</div>
                  <button onClick={() => setDeadline.mutate(null)} disabled={setDeadline.isPending} className="text-[12.5px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium">
                    Reopen RSVPs
                  </button>
                </div>
              ) : rsvpClosesAt ? (
                <div>
                  <div className="text-[12.5px] text-ink-2 mb-2">Closes {formatDeadline(occurrence.rsvpClosesAt!)}</div>
                  <div className="flex gap-3">
                    <button onClick={() => setDeadline.mutate(null)} disabled={setDeadline.isPending} className="text-[12.5px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium">Remove deadline</button>
                    <span className="text-muted text-[12px]">·</span>
                    <button onClick={() => setDeadline.mutate(new Date().toISOString())} disabled={setDeadline.isPending} className="text-[12.5px] text-warn cursor-pointer bg-transparent border-0 p-0 font-sans font-medium">Close now</button>
                  </div>
                </div>
              ) : showDeadlinePicker ? (
                <div className="flex flex-col gap-2">
                  <input
                    type="datetime-local"
                    aria-label="RSVP deadline"
                    value={deadlineInput}
                    onChange={(e) => setDeadlineInput(e.target.value)}
                    className="px-2.5 py-2 rounded-lg border border-line-soft bg-s2 text-[12.5px] font-sans w-full"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => deadlineInput && setDeadline.mutate(new Date(deadlineInput).toISOString(), {
                        onSuccess: () => { setShowDeadlinePicker(false); setDeadlineInput('') },
                      })}
                      disabled={setDeadline.isPending || !deadlineInput}
                      className="text-[12.5px] text-primary cursor-pointer bg-transparent border-0 p-0 font-sans font-medium disabled:opacity-50"
                    >
                      {setDeadline.isPending ? 'Saving…' : 'Set deadline'}
                    </button>
                    <span className="text-muted text-[12px]">·</span>
                    <button onClick={() => setShowDeadlinePicker(false)} className="text-[12.5px] text-muted cursor-pointer bg-transparent border-0 p-0 font-sans">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => setShowDeadlinePicker(true)} className="text-[12.5px] text-ink-2 cursor-pointer bg-transparent border-0 p-0 font-sans font-medium hover:text-primary">Set deadline</button>
                  <span className="text-muted text-[12px]">·</span>
                  <button onClick={() => setDeadline.mutate(new Date().toISOString())} disabled={setDeadline.isPending} className="text-[12.5px] text-warn cursor-pointer bg-transparent border-0 p-0 font-sans font-medium">Close now</button>
                </div>
              )}
            </AdminCard>
          )}

          {/* Move to draft */}
          {!isDraft && (
            <AdminCard title="Status">
              <div className="text-[12.5px] text-muted mb-2.5">This event is live and visible in Discover.</div>
              <AdmBtn variant="soft" size="sm" disabled={draft.isPending} onClick={() => draft.mutate()}>
                {draft.isPending ? 'Saving…' : 'Move to draft'}
              </AdmBtn>
            </AdminCard>
          )}
        </div>
      </div>
    </AdminShell>
  )
}
